import { createLogger } from '@fabric-app/utils/logging';
import { ensureError } from '@fabric-app/utils/guards';
import { getDeviceType } from '../connections.constants.js';
import { handleUpload } from '../../upload/utils/handleUpload.js';

const logger = createLogger("Screenshot Image Sync");
class SyncImageError extends Error {
  constructor(code, message, originalError = null) {
    super(message);
    this.name = "SyncImageError";
    this.code = code;
    this.originalError = originalError;
  }
}
const syncImageWithServer = async (imageResponse, config) => {
  const { client, imageAccess, user, mirrorRegistryId } = config;
  if (!("uniqueId" in imageAccess) || imageAccess.uniqueId === void 0) {
    throw new Error("Missing unique id in image access");
  }
  if (getDeviceType() === null) {
    throw new Error("Unknown device type");
  }
  if (imageResponse === null) {
    logger.debug("Missing image in image response, skipping upload");
    return Promise.resolve({
      code: "SKIP_MISSING_IMAGE_IN_RESPONSE"
    });
  }
  const isUploadedResponse = await client.v2({
    endpoint: "/v2/mirror-sync/{mirrorRegistryId}/hash",
    params: {
      mirrorRegistryId
    }
  }, {
    method: "post",
    body: {
      hashes: [
        imageResponse.localIdentifier
      ]
    }
  });
  if (isUploadedResponse[imageResponse.localIdentifier] === true) {
    logger.debug("Image already uploaded, skipping");
    return Promise.resolve({
      code: "ALREADY_UPLOADED"
    });
  }
  const type = imageResponse.name.split(".").pop();
  const nameWithoutExtension = imageResponse.name.replace(/\.[^/.]+$/, "");
  const fileParams = "fileUri" in imageResponse ? {
    type: "uri",
    file: {
      uri: imageResponse.fileUri,
      size: 0,
      name: imageResponse.name,
      mimeType: `image/${type ?? "png"}`
    },
    available: true
  } : {
    type: "file",
    file: new File([
      Buffer.from(imageResponse.base64String, "base64")
    ], imageResponse.name, {
      type: `image/${type ?? "png"}`
    }),
    available: true
  };
  const { key } = await handleUpload({
    bucket: "stored-files",
    data: fileParams,
    userTier: user.subscription.tier
  });
  try {
    await client.v2({
      endpoint: "/v2/mirror-sync/{mirrorRegistryId}/item",
      params: {
        mirrorRegistryId
      }
    }, {
      method: "post",
      body: {
        sourceType: "SCREENSHOT",
        data: {
          hash: imageResponse.localIdentifier,
          key,
          originUrl: null,
          takenAt: imageResponse.creationDate,
          title: nameWithoutExtension || "Untitled"
        }
      }
    });
  } catch (error) {
    return Promise.reject(new SyncImageError("FAILED_TO_CREATE_RECORD", "Failed to create record", ensureError(error)));
  }
  return Promise.resolve({
    code: "UPLOADED"
  });
};

export { syncImageWithServer };
//# sourceMappingURL=syncImageWithServer.js.map
